// What the drive may do, asked of whoever owns the hull. Cruise flies whatever
// this answers and reads no heat, power or hull of its own; play answers from
// the heat ladder (`heatDriveLimits`), a sim with no game on top from
// NO_DRIVE_LIMITS.

import { departureSplit, sustainedThrottle, thermalThrottle, tripHoldThrottle, warpEaseThrottle } from '../scene/heat.ts';

export interface DriveLimits {
  /** The most of the drive this instant will deliver, [0, 1]. 1 is no cap. */
  readonly throttleCap: number;
  /** Share of full thrust a burn opened from here holds once it has run long
   *  enough to heat the hull, which an idle ship's `throttleCap` cannot show
   *  yet. What the autopilot times a departure at. */
  readonly burnThrustShare: number;
}

/** Asked afresh at every read: the answer moves as the hull heats mid-frame.
 *  `trip` is a burn the autopilot flies, which is held to the share it was
 *  planned on rather than to the ladder a hand on the throttle meets. */
export type DriveLimitsFn = (trip?: boolean) => DriveLimits;

export const NO_DRIVE_LIMITS: DriveLimits = Object.freeze({ throttleCap: 1, burnThrustShare: 1 });

export const noDriveLimits: DriveLimitsFn = () => NO_DRIVE_LIMITS;

/** What the heat ladder lets the drive do: the ladder's own cap on a full bar,
 *  held from rung 2 up to what the panels as they stand can shed, eased under
 *  warp further still, none of it while the player pushes past the red line,
 *  and a burn's share at the posture the panels are headed for. A `trip` out
 *  of a fight flies that share at the posture the panels stand at, whatever
 *  the warp and the rung, so the burn is the one it was timed on. `shedHold`
 *  false is heat-trip-check's control, the flat cap a stowed hull cooks at. */
export function heatDriveLimits(
  rung: number, panelsPerSide: number, deployTarget: number, deployFraction: number, warped: boolean,
  pastRedLine = false, shedHold = true, trip = false,
): DriveLimits {
  const flat = thermalThrottle(1, rung, 1, pastRedLine);
  const ladder = shedHold && flat < 1 ? Math.min(flat, tripHoldThrottle(panelsPerSide, deployFraction)) : flat;
  const eased = warped && !pastRedLine;
  const hand = eased ? Math.min(ladder, warpEaseThrottle(panelsPerSide, deployFraction)) : ladder;
  const share = sustainedThrottle(panelsPerSide, deployFraction);
  const flown = !trip || pastRedLine || departureSplit() ? hand : shedHold ? share : Math.min(hand, share);
  return {
    throttleCap: flown,
    burnThrustShare: sustainedThrottle(panelsPerSide, deployTarget),
  };
}

/** Where GO leaves the radiators: out, unless they already are or GO is told
 *  to keep the posture it finds. */
export function goDeployTarget(deployTarget: number, keepsPosture = false): number {
  return keepsPosture || deployTarget > 0.5 ? deployTarget : 1;
}

/**
 * The control: the delta-v map prices a trip's burns at full thrust again,
 * whatever the radiators let the drive hold. Set by `?fullthrustprice=1` and by
 * tools/preview/transit-check.mjs --inject full-thrust-price.
 */
let fullThrustPriceInjected = false;
export function setFullThrustPrice(on: boolean): void {
  fullThrustPriceInjected = on;
}

/** Share of full thrust the delta-v map prices a trip's burns at: the burn
 *  share (`burnThrustShare`) at the posture GO will leave the radiators in,
 *  which the autopilot then flies every burn of the trip at. */
export function tripPriceShare(panelsPerSide: number, goTarget: number): number {
  return fullThrustPriceInjected ? 1 : sustainedThrottle(panelsPerSide, goTarget);
}

/** A commanded throttle on cruise's [-1, 1] bar, held to the cap either way.
 *  A cap of 1 passes the command through untouched. */
export function capThrottle(commanded: number, cap: number): number {
  if (cap >= 1) return commanded;
  return commanded > cap ? cap : commanded < -cap ? -cap : commanded;
}
